/**
 * SMC overlay renderer — Order Blocks, Fair Value Gaps, Liquidity pools,
 * BOS / CHoCH structure markers, and key swing levels.
 *
 * This is a port of the SMC drawing logic that previously lived inline in
 * `ChartNeonOverlay`, refactored to take an explicit `OverlayDrawContext`
 * so it can be reused by the methodology-aware overlay dispatcher and by
 * future export pipelines.
 */
import type { SMCOverlay } from '@trading/shared';
import type { OverlayDrawContext } from './types';

export function drawSMCOverlay(data: SMCOverlay, c: OverlayDrawContext): void {
  const { ctx, height, defaultZoneStartX, zoneEndX, getYFromPrice, getXFromTime } = c;

  // Order Blocks — translucent rectangles starting at the OB candle.
  data.orderBlocks?.forEach((ob) => {
    const topY = getYFromPrice(ob.top);
    const bottomY = getYFromPrice(ob.bottom);
    if (topY === null || bottomY === null) return;

    const y = Math.min(topY, bottomY);
    const h = Math.abs(bottomY - topY);
    if (!Number.isFinite(y) || !Number.isFinite(h) || h < 1) return;

    const obStartX = getXFromTime(ob.time, defaultZoneStartX);
    const obWidth = Math.max(zoneEndX - obStartX, 80);

    ctx.save();
    const color = ob.type === 'bullish' ? '#22c55e' : '#ef4444';
    const alpha = ob.status === 'fresh' ? 0.12 : ob.status === 'tested' ? 0.08 : 0.04;
    ctx.fillStyle = `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    ctx.fillRect(obStartX, y, obWidth, h);
    ctx.fillStyle = `${color}80`;
    ctx.fillRect(obStartX, y, 2, h);
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(ob.type === 'bullish' ? 'OB+' : 'OB-', obStartX + 6, y + 4);
    ctx.restore();
  });

  // Fair Value Gaps — dashed-border rectangles.
  data.fvg?.forEach((gap) => {
    const topY = getYFromPrice(gap.top);
    const bottomY = getYFromPrice(gap.bottom);
    if (topY === null || bottomY === null) return;
    const y = Math.min(topY, bottomY);
    const h = Math.abs(bottomY - topY);
    if (!Number.isFinite(y) || !Number.isFinite(h) || h < 2) return;

    const fvgStartX = getXFromTime(gap.time, defaultZoneStartX);
    const fvgWidth = Math.max(zoneEndX - fvgStartX, 80);

    ctx.save();
    const color = gap.type === 'bullish' ? '#3b82f6' : '#f97316';
    const alpha = gap.filledPercent < 50 ? 0.1 : 0.05;
    ctx.fillStyle = `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    ctx.fillRect(fvgStartX, y, fvgWidth, h);
    ctx.strokeStyle = `${color}60`;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(fvgStartX, y, fvgWidth, h);
    ctx.setLineDash([]);
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('FVG', fvgStartX + 6, y + 4);
    ctx.restore();
  });

  // Liquidity Pools (BSL/SSL) — dotted horizontal lines.
  data.liquidity?.forEach((liq) => {
    const y = getYFromPrice(liq.price);
    if (y === null || y < 0 || y > height) return;
    const liqStartX = getXFromTime(liq.time, defaultZoneStartX);
    ctx.save();
    const color = liq.type === 'bsl' ? '#a855f7' : '#ec4899';
    ctx.strokeStyle = `${color}80`;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 5]);
    ctx.beginPath();
    ctx.moveTo(liqStartX, y);
    ctx.lineTo(zoneEndX, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(liq.type === 'bsl' ? '$$$ BSL' : '$$$ SSL', liqStartX + 6, y);
    ctx.restore();
  });

  // Key swing levels.
  if (data.keyLevels) {
    const keyLevels = [
      { price: data.keyLevels.swingHigh, label: 'Swing H' },
      { price: data.keyLevels.swingLow, label: 'Swing L' },
    ];
    keyLevels.forEach(({ price, label }) => {
      if (!Number.isFinite(price)) return;
      const y = getYFromPrice(price);
      if (y === null || y < 0 || y > height) return;
      ctx.save();
      ctx.strokeStyle = '#facc1550';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(defaultZoneStartX, y);
      ctx.lineTo(zoneEndX, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.fillStyle = '#facc1590';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, defaultZoneStartX + 6, y);
      ctx.restore();
    });
  }

  // BOS / CHoCH structure markers.
  if (data.bos) {
    const y = getYFromPrice(data.bos.price);
    if (y !== null && y >= 0 && y <= height) {
      ctx.save();
      const color = data.bos.type === 'bullish' ? '#22c55e' : '#ef4444';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = color;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.fillText('BOS', zoneEndX - 5, y);
      ctx.restore();
    }
  }
  if (data.choch) {
    const y = getYFromPrice(data.choch.price);
    if (y !== null && y >= 0 && y <= height) {
      ctx.save();
      const color = data.choch.type === 'bullish' ? '#22c55e' : '#ef4444';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = color;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.fillText('CHoCH', zoneEndX - 5, y);
      ctx.restore();
    }
  }
}
